/**
 * Conditions — the WIND and WEATHER picture for Plotting. The tide side (heights
 * and streams, their sources, the port card and the curve) now lives in its own
 * clean two-tab Tides sheet, so this sheet is just the wind readout, the chart
 * overlay swap (tidal streams vs weather), and the weather source with its
 * real-model forecast slider. A button jumps straight over to Tides.
 */
import { useEffect } from 'react';
import { useChartPlotterStore } from '@/store';
import { useLocalConditionsStore } from '@/store/localConditions';
import { douglasSeaState } from '@/weather/seaState';
import type { RealModelId } from '@/weather/realWeather';
import { useEnvStore } from '@/store/environment';
import { useShellStore } from '@/store/shell';
import { useRealWeatherStore } from '@/store/realWeather';
import { formatBearing } from '@/nav';
import { WEATHER_SOURCES } from '@/weather';
import { Sheet } from './Sheet';
import { providerHasRealWeather } from '@/chart-sources';

function fmtForecast(offsetH: number): string {
  const d = new Date(Date.now() + offsetH * 3_600_000);
  const wd = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getUTCDay()];
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UT, ${wd}`;
}

/**
 * What it is doing HERE, now (or at the forecast hour).
 *
 * The barb field is a grid over the visible chart and is only fetched while the
 * weather overlay is up in Plotting, so opening Conditions under way in Live mode
 * loaded nothing at all and the sheet had a source picker and no numbers. This
 * asks for the one point that matters instead: the boat.
 *
 * Everything here is allowed to be absent and says so. The marine model covers
 * open sea, so up a loch or on a pontoon there is no wave forecast, and "no
 * forecast" must not come out as a flat calm.
 */
function HereNow({ position, modelId, targetMs }: {
  position: [number, number];
  modelId: RealModelId;
  targetMs: number;
}) {
  const load = useLocalConditionsStore((s) => s.load);
  const fields = useLocalConditionsStore((s) => s.fields);
  const status = useLocalConditionsStore((s) => s.status);
  const error = useLocalConditionsStore((s) => s.error);

  // Rounded, so drifting round an anchorage does not re-run this on every fix.
  const lon = Number(position[0].toFixed(2));
  const lat = Number(position[1].toFixed(2));
  useEffect(() => {
    void load([lon, lat], modelId);
  }, [load, lon, lat, modelId]);

  const here = fields ? useLocalConditionsStore.getState().at(targetMs) : null;

  if (status === 'loading' && !here) return <p className="acp-note">Fetching the forecast for here…</p>;
  if (status === 'error') {
    return <p className="acp-note">Couldn’t fetch the forecast for here: {error ?? 'network error'}.</p>;
  }
  if (!here) return null;

  const sea = here.sea;
  const band = sea ? douglasSeaState(sea.waveHeightM) : null;

  return (
    <>
      <label className="acp-fieldlabel" style={{ marginTop: 12 }}>Here</label>
      <div className="acp-kv">
        <span className="k">Wind</span>
        <span className="v">
          {here.windFromDeg == null || here.windKn == null
            ? 'not forecast'
            : `${formatBearing(here.windFromDeg)} at ${here.windKn.toFixed(0)} kn`}
        </span>
      </div>
      <div className="acp-kv">
        <span className="k">Gusting</span>
        <span className="v">{here.gustKn == null ? 'not forecast' : `${here.gustKn.toFixed(0)} kn`}</span>
      </div>
      <div className="acp-kv">
        <span className="k">Pressure</span>
        <span className="v">
          {here.pressureHpa == null ? 'not forecast' : `${here.pressureHpa.toFixed(0)} hPa`}
        </span>
      </div>
      {sea && band ? (
        <>
          <div className="acp-kv">
            <span className="k">Sea</span>
            <span className="v">
              {sea.waveHeightM.toFixed(1)} m · {band.label}
              {sea.waveFromDeg != null && ` · from ${formatBearing(sea.waveFromDeg)}`}
              {sea.wavePeriodS != null && ` every ${sea.wavePeriodS.toFixed(0)} s`}
            </span>
          </div>
          <div className="acp-kv">
            <span className="k">Swell</span>
            <span className="v">
              {sea.swellHeightM > 0
                ? `${sea.swellHeightM.toFixed(1)} m from ${formatBearing(sea.swellFromDeg)} every ${sea.swellPeriodS.toFixed(0)} s`
                : 'none forecast'}
            </span>
          </div>
        </>
      ) : (
        <p className="acp-note">
          No wave forecast for this spot. The marine model covers open sea, so inshore, up a loch or
          alongside there is nothing to report, which is not the same as a flat calm.
        </p>
      )}
    </>
  );
}

export function ConditionsSheet() {
  const openSheet = useShellStore((s) => s.openSheet);
  const streamOn = useChartPlotterStore((s) => !!s.groupVisibility['tidal-stream']);
  const toggleGroup = useChartPlotterStore((s) => s.toggleGroup);
  const chartProviderId = useChartPlotterStore((s) => s.chartProviderId);
  const ownPosition = useChartPlotterStore((s) => s.ownship.position);

  const windDir = useEnvStore((s) => s.windDirDeg);
  const windKn = useEnvStore((s) => s.windSpeedKn);
  const overlayMode = useEnvStore((s) => s.overlayMode);
  const setOverlayMode = useEnvStore((s) => s.setOverlayMode);
  const weatherSource = useEnvStore((s) => s.weatherSource);
  const setWeatherSource = useEnvStore((s) => s.setWeatherSource);
  const weatherForecastH = useEnvStore((s) => s.weatherForecastH);
  const setWeatherForecastH = useEnvStore((s) => s.setWeatherForecastH);

  const realWxStatus = useRealWeatherStore((s) => s.status);
  const realWxError = useRealWeatherStore((s) => s.error);
  // Real forecast models apply wherever the chart shows real water, not only on the
  // Antares charts. The NOAA cells are real US water and were being denied a real
  // forecast for no reason other than a hardcoded provider id.
  const realArea = providerHasRealWeather(chartProviderId);

  return (
    <Sheet
      name="conditions"
      title="Conditions"
      sub="The wind and weather picture for this passage. Tides (heights and streams) have their own screen."
    >
      <div className="acp-kv">
        <span className="k">Wind from</span>
        <span className="v">
          {formatBearing(windDir)} at {windKn.toFixed(0)} kn
        </span>
      </div>

      <div className="acp-btn-row" style={{ marginTop: 12 }}>
        <button type="button" className="acp-btn full" onClick={() => openSheet('tides')}>
          Tides — heights &amp; streams
        </button>
      </div>

      <label className="acp-fieldlabel" style={{ marginTop: 12 }}>Chart overlay</label>
      <div className="acp-seg">
        <button
          type="button"
          className={overlayMode === 'streams' ? 'on' : ''}
          onClick={() => setOverlayMode('streams')}
        >
          Tidal streams
        </button>
        <button
          type="button"
          className={overlayMode === 'weather' ? 'on' : ''}
          onClick={() => setOverlayMode('weather')}
        >
          Weather
        </button>
      </div>
      {overlayMode === 'streams' ? (
        <div className="acp-layer" style={{ marginTop: 8 }}>
          <div className="lt">
            <b>Tidal-stream overlay</b>
            <span>The arrows and the time scrubber show here while plotting.</span>
          </div>
          <button
            type="button"
            className={`acp-sw${streamOn ? ' on' : ''}`}
            aria-pressed={streamOn}
            aria-label="Tidal stream overlay"
            onClick={() => toggleGroup('tidal-stream')}
          />
        </div>
      ) : (
        <>
          <label className="acp-fieldlabel" style={{ marginTop: 8 }}>Weather source</label>
          <div className="acp-seg" style={{ flexWrap: 'wrap' }}>
            {WEATHER_SOURCES.map((w) => {
              const enabled = !w.realArea || realArea;
              return (
                <button
                  key={w.id}
                  type="button"
                  className={weatherSource === w.id ? 'on' : ''}
                  disabled={!enabled}
                  onClick={() => enabled && setWeatherSource(w.id)}
                  title={w.character}
                >
                  {w.name} · {w.realArea ? 'free' : 'free'}
                </button>
              );
            })}
          </div>
          {weatherSource === 'synthetic' ? (
            <p className="acp-note">
              Synthetic training wind over the fictional area, tracking the time scrubber (use Play on
              the scrubber to watch it evolve). Not a real forecast.
            </p>
          ) : (
            <p className="acp-note">
              Real 10 m wind for this area from{' '}
              {WEATHER_SOURCES.find((w) => w.id === weatherSource)?.name ?? weatherSource} (via
              Open-Meteo, free,
              CC-BY).{' '}
              {realWxStatus === 'loading' && 'Fetching…'}
              {realWxStatus === 'error' && `Couldn’t fetch: ${realWxError ?? 'network error'}.`}
            </p>
          )}
          {!realArea && (
            <p className="acp-note">
              Real models (GFS, ECMWF, ICON) apply on charts of real water. The training charts are a
              made-up place, so there is no forecast for them and the wind stays synthetic. Switch the
              chart source to a real one to use them.
            </p>
          )}
          {realArea && weatherSource !== 'synthetic' && (
            <>
              <label className="acp-fieldlabel" style={{ marginTop: 8 }}>
                Forecast time · {weatherForecastH === 0 ? 'now' : `+${weatherForecastH} h`}
              </label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                  type="button"
                  className="acp-btn"
                  aria-label="Forecast one hour earlier"
                  onClick={() => setWeatherForecastH(Math.max(0, weatherForecastH - 1))}
                >
                  −1h
                </button>
                <input
                  type="range"
                  min={0}
                  max={72}
                  step={1}
                  value={weatherForecastH}
                  aria-label="Forecast time, hours from now"
                  style={{ flex: 1 }}
                  onChange={(e) => setWeatherForecastH(Number(e.target.value))}
                />
                <button
                  type="button"
                  className="acp-btn"
                  aria-label="Forecast one hour later"
                  onClick={() => setWeatherForecastH(Math.min(72, weatherForecastH + 1))}
                >
                  +1h
                </button>
              </div>
              <p className="acp-note">
                Wind{' '}
                {weatherForecastH === 0
                  ? 'now'
                  : `in ${weatherForecastH} ${weatherForecastH === 1 ? 'hour' : 'hours'}`}{' '}
                ({fmtForecast(weatherForecastH)}). Real conditions out to 72 h, instant from the cached
                field.
              </p>
              {ownPosition && (
                <HereNow
                  position={ownPosition}
                  modelId={weatherSource}
                  targetMs={Date.now() + weatherForecastH * 3_600_000}
                />
              )}
            </>
          )}
          <div className="acp-btn-row">
            <button type="button" className="acp-btn full" onClick={() => openSheet('forecast')}>
              View forecasts and MSI
            </button>
          </div>
        </>
      )}
    </Sheet>
  );
}
